import React, { useEffect, useState } from 'react';
import { AlertCircle, Download, Loader2 } from 'lucide-react';

import { warehouseApi as api } from 'api/warehouse';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { BulkUploadDropzone } from 'shared/components/bulkUpload';

const PACK_LINE_OOS_REASONS = [
    'Vendor Short - Case Short, Ct/Cs discrepancy',
    'Inventory Accuracy (Ops related)',
    'Inventory Accuracy (Planning related)',
    'Gapping',
    'Late Truck',
    'Quality Issue - Rejection, QC pull',
    'No more inventory on hand',
    'Late Truck - DLVD',
    'Other',
];

const TEMPLATE_CSV = 'external_sku\n';

function downloadTemplate() {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pack_line_bulk_oos_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * One-shot dialog to bulk-mark pack-line slots OUT_OF_STOCK via CSV upload.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {number|string} props.packLineId
 * @param {(result: { successCount: number }) => void} [props.onSuccess]
 */
function BulkMarkPackLineOutOfStock({ onOpenChange, onSuccess, open, packLineId }) {
    const [reason, setReason] = useState('');
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dropzoneKey, setDropzoneKey] = useState(0);

    useEffect(() => {
        if (!open) {
            return;
        }
        setReason('');
        setFile(null);
        setError('');
        setIsSubmitting(false);
        setDropzoneKey((key) => key + 1);
    }, [open, packLineId]);

    const canSubmit = Boolean(reason && file && packLineId != null && !isSubmitting);

    const handleOpenChange = (nextOpen) => {
        if (isSubmitting && !nextOpen) {
            return;
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = async () => {
        if (!canSubmit) {
            return;
        }

        setError('');
        setIsSubmitting(true);
        try {
            const res = await api.bulkMarkPackLineSlotsOutOfStock({
                packLineId,
                reason,
                file,
            });

            if (!res.status) {
                setError(res.msg || 'Failed to mark items out of stock');
                return;
            }

            onSuccess?.({ successCount: res.data?.successCount ?? 0 });
            onOpenChange(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="sm:max-w-lg"
                onEscapeKeyDown={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
                onPointerDownOutside={(e) => {
                    if (isSubmitting) {
                        e.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>Bulk mark out of stock</DialogTitle>
                    <DialogDescription>
                        Upload a CSV of external SKUs to mark matching slots on this pack line out of stock. All SKUs
                        must already exist on the line (all-or-nothing).
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="bulk-oos-reason">Reason</Label>
                        <Select value={reason} onValueChange={setReason} disabled={isSubmitting}>
                            <SelectTrigger id="bulk-oos-reason" className="w-full" aria-label="Reason">
                                <SelectValue placeholder="Select a reason" />
                            </SelectTrigger>
                            <SelectContent>
                                {PACK_LINE_OOS_REASONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                            <Label>CSV file</Label>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={downloadTemplate}
                                disabled={isSubmitting}
                            >
                                <Download className="mr-2 h-4 w-4" />
                                Download Template
                            </Button>
                        </div>
                        <BulkUploadDropzone
                            key={dropzoneKey}
                            onFileSelect={setFile}
                            disabled={isSubmitting}
                            isUploading={isSubmitting}
                        />
                        <p className="text-xs text-muted-foreground">
                            Required column: <code className="rounded bg-muted px-1">external_sku</code> (one SKU per
                            row).
                        </p>
                    </div>

                    {error ? (
                        <Alert variant="destructive">
                            <AlertCircle />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : null}
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Marking…
                            </>
                        ) : (
                            'Mark out of stock'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export { BulkMarkPackLineOutOfStock, PACK_LINE_OOS_REASONS };
